import React, { useContext, useState, useEffect, createContext } from 'react';
import CreateCloudfrontUrl from '/src/services/CreateCloudfrontUrl';
import GaugeChart from 'react-gauge-chart';
import { CurlyBraceDivider } from '/src/Components/CurlyBraceDivider/CurlyBraceDivider';
import ProjectsSummaryData from '/src/Components/ProjectsSummaryData/ProjectsSummaryData';
import ProjectsListCard from '/src/Components/ProjectsListCard/ProjectsListCard';
import { AreaDataContext } from '../PropertyExplorer';
import { WindowSizeContext } from '/src/App';
import './style.css';



const BigPicture = ({
    propertyData
}) => {

    const { goToRiskOverview, goToYourOptions, anonQuollScoreAnalysis, quollScoreAnalysis } = useContext(AreaDataContext);
    const { deviceType } = useContext(WindowSizeContext);

    const impactAcrossTimeOptions = [
        {
            id: 1,
            icon: 'broken-home-green.png',
            timeframe: 'Sudden',
            title: 'Unforeseen Fixes',
            description: "Costs you may not have budgeted for.",
            gaugeValue: 0
        },
        {
            id: 2,
            icon: 'home_bills.png',
            timeframe: 'Recurring',
            title: 'General Expenses',
            description: 'Think insurance, maintenance, taxes, utilities etc.',
            gaugeValue: 0
        },
        {
            id: 3,
            icon: 'home-ltv-icon.png',
            timeframe: 'Long-Term',
            title: 'Market Value',
            description: 'What happens when you go to sell the property?',
            gaugeValue: 0
        }
    ]

    const [completeImpactAcrossTimeOptions, setCompleteImpactAcrossTimeOptions] = useState(impactAcrossTimeOptions);

    // useEffect to monitor the quollScoreAnalysis and anonQuollScoreAnalysis
    useEffect(() => {
        // Create a copy of the impactAcrossTimeOptions
        const impactAcrossTimeOptionsCopy = [...impactAcrossTimeOptions];

        let quollScoreDetails = null;
        if (quollScoreAnalysis) {
            quollScoreDetails = quollScoreAnalysis.quollScore

        } else if (anonQuollScoreAnalysis) {
            quollScoreDetails = anonQuollScoreAnalysis.quollScore
        }

        if (quollScoreDetails) {
            const { budgetScore, opexScore, ltvCombinedScore } = quollScoreDetails;
            impactAcrossTimeOptionsCopy[0].gaugeValue = budgetScore;
            impactAcrossTimeOptionsCopy[1].gaugeValue = opexScore;
            impactAcrossTimeOptionsCopy[2].gaugeValue = ltvCombinedScore;
            setCompleteImpactAcrossTimeOptions(impactAcrossTimeOptionsCopy);
        }
    }, [quollScoreAnalysis, anonQuollScoreAnalysis]);

    const introSentence = "You can't change what climate risks your home faces. But you can change how resilient your home is and how much climate change will cost you."

    // const riskSectionHeader = "These are the risks that will cost you money if you aren't prepared..."
    let riskSectionHeader = "These are the risks that will hit your wallet if you aren't prepared..."
    let projectsSectionHeader = "This is how we help you save money and protect your future..."

    if (deviceType === 'mobile') {
        riskSectionHeader = "These are the risks that will hit your wallet if you aren't prepared..."
        projectsSectionHeader = "This is how we help you save money and protect your future..."
    }

    const riskProfileDeepDiveButtonText = "See Risk Profile Deep Dive"
    const projectsDeepDiveButtonText = "See How To Start Saving"

    const climateRisks = propertyData?.data?.climate_risks || [];

    // Sort the risks (High --> Medium --> Low based on the risk_level key)
    const sortedRisks = climateRisks.sort((a, b) => {
        const order = ['High', 'Medium', 'Low'];
        return order.indexOf(a.risk_level) - order.indexOf(b.risk_level);
    });

    const getRiskBackgroundColor = (riskLevel) => {
        switch (riskLevel.toLowerCase()) {
            case 'high':
                return 'var(--related_red)';
            case 'medium':
                return 'var(--yellow)';
            case 'low':
                return 'var(--accent_light)';
            default:
                return 'transparent';
        }
    };

    const needleColors = [
        "#34C759",
        "#66C237",
        "#93BD15",
        "#edf051",
        "#ffe570",
        "#FFD60A",
        "#FFB007",
        "#FF8A04",
        "#FF5C31",
        "#FF3B30"
    ]

    const getNeedleColor = (level) => {
        return needleColors[level - 1];
    }

    const projects_list = propertyData?.data?.recommended_projects?.projects_list || [];

    // console.log(projects_list);
    // console.log(sortedRisks);

    const getTopProjectsByRisks = (projects_list, sortedRisks) => {
        const topProjects = [];
        let riskIndex = 0;

        // Create a map of risk names to full risk objects for easy lookup
        const riskMap = sortedRisks.reduce((map, risk) => {
            map[risk.risk_name] = risk;
            return map;
        }, {});

        // Keep going until we have 3 projects or run out of risks to check
        const maxProjects = 4;
        while (topProjects.length < maxProjects && riskIndex < sortedRisks.length) {
            const currentRisk = sortedRisks[riskIndex].risk_name;

            // Find projects that address the current risk
            const projectsForRisk = projects_list
                .filter(project =>
                    // Check if project addresses this risk
                    project.project_associated_climate_risks.includes(currentRisk) &&
                    // Make sure we haven't already selected this project
                    !topProjects.includes(project)
                )
                .map(project => ({
                    ...project,
                    // Get the total payoff value for sorting
                    payoff: project.project_payoff_by_scenario[0].by_total.total,
                    // Replace risk strings with full risk objects
                    project_associated_climate_risks: project.project_associated_climate_risks.map(
                        riskName => riskMap[riskName]
                    )
                }))
                .sort((a, b) => b.payoff - a.payoff); // Sort by payoff in descending order

            // Add projects from this risk until we hit our limit of 3
            for (const project of projectsForRisk) {
                if (topProjects.length < maxProjects) {
                    topProjects.push(project);
                } else {
                    break;
                }
            }

            riskIndex++;
        }

        return topProjects;
    };

    // Usage:
    const topThreeProjects = getTopProjectsByRisks(projects_list, sortedRisks);

    // console.log(projects_list, sortedRisks, topThreeProjects);

    return (
        <div className={`big-picture-content-container ${deviceType}`}>
            <h1 className="text-align-center">Big Picture For This Home</h1>
            <p className="big-picture-intro-sentence text-align-center" dangerouslySetInnerHTML={{ __html: introSentence }} />
            <div className="big-picture-sections-container">
                <div className="big-picture-individual-section-container">
                    <h3 className="big-picture-individual-section-header text-align-center">{riskSectionHeader}</h3>

                    <div className={`big-picture-climate-risks-container ${deviceType}`}>
                        {sortedRisks.map((risk, index) => {
                            const riskName = risk.risk_name;

                            // Take the riskName, split at the space (making sure to strip out any extra spaces), turn into an array
                            const riskNameArray = riskName.split(' ').filter(Boolean);
                            return (
                                <div className="big-picture-climate-risks-item" key={index}>
                                    <div
                                        className="big-picture-climate-risks-icon-container box-shadow-standard"
                                        style={{
                                            backgroundColor: getRiskBackgroundColor(risk.risk_level)
                                        }}
                                    >
                                        <img
                                            src={`${import.meta.env.VITE_CONTENT_CDN_BASE_URL}/${risk.icon_white}`}
                                            alt={risk.risk_name}
                                            className="risk-icon"
                                        />
                                    </div>
                                    <div className="big-picture-climate-risks-item-risk-name-container">
                                        {riskNameArray.map((word, index) => (
                                            <span key={index} className="risk-name">{word}</span>
                                        ))}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                    <CurlyBraceDivider color='grey' />
                    <div className="big-picture-climate-risks-plus-impact-container">
                        <span className="big-picture-projects-list-header text-align-center">How Worried Should You Be? </span>

                        <div className="big-picture-financial-impact-container">
                            {completeImpactAcrossTimeOptions.map((option, index) => (
                                <div key={index} className="how-to-think-impact-across-time-option">
                                    <div className="how-to-think-impact-across-time-option-timeframe-container">
                                        <span>{option.timeframe}</span>
                                    </div>
                                    <div className="how-to-think-impact-across-time-option-icon-wrapper">
                                        <img src={CreateCloudfrontUrl(option.icon)} alt={option.title} />
                                    </div>
                                    <div className="how-to-think-impact-across-time-option-text-wrapper">
                                        <span className="how-to-think-impact-across-time-option-title">{option.title}</span>
                                        <span className="how-to-think-impact-across-time-option-description big-picture text-align-center">{option.description}</span>
                                    </div>
                                    <div className="big-picture-financial-impact-option-gauge-container">
                                        <GaugeChart
                                            id={`big-picture-financial-impact-option-gauge`}
                                            nrOfLevels={10}
                                            colors={needleColors}
                                            arcWidth={0.25}
                                            percent={option.gaugeValue / 10}
                                            textColor="transparent"
                                            needleScale={.65}
                                            needleColor={getNeedleColor(option.gaugeValue)}
                                            needleBaseColor="#666666"
                                            className="big-picture-financial-impact-option-gauge"
                                            hideText={true}
                                            style={{
                                                height: '100%',
                                            }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <button
                        className="big-picture-financial-impact-option-cta-button text-align-center"
                        onClick={goToRiskOverview}
                    >
                        {riskProfileDeepDiveButtonText}
                    </button>
                </div>
                <div className="big-picture-individual-section-container">
                    <h3 className="big-picture-individual-section-header text-align-center">{projectsSectionHeader}</h3>
                    <ProjectsSummaryData
                        projects_list={projects_list}
                        propertyData={propertyData}
                        isBigPicture={true}
                    />
                    <div className="big-picture-projects-container">
                        <span className="big-picture-projects-list-header text-align-center">Top Projects For This Home: </span>
                        <div className={`big-picture-projects-list-container ${deviceType}`}>
                            {topThreeProjects.map((project, index) => (
                                <ProjectsListCard
                                    key={index}
                                    project={project}
                                    isBigPicture={true}
                                />
                            ))}
                        </div>
                    </div>
                    <button
                        className="big-picture-financial-impact-option-cta-button text-align-center"
                        onClick={goToYourOptions}
                    >
                        {projectsDeepDiveButtonText}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BigPicture;